import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import { showNotification } from "aldoo-ra/Notification"
import { SubmitSupportTicket } from "aldoo-ra/Admin/CustomerSupport/api"
import PubSub from "pubsub-js"
import emailValidator from "email-validator"

const CustomerSupportPanel = () => {
  const texts = TextBundle({ id: "customer_support_dialog" })
  const { t } = Localization()
  const [questionType, setQuestionType] = useState("")
  const [message, setMessage] = useState("")
  const [email, setEmail] = useState("")
  const [file, setFile] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const fileInputRef = useRef(null)

  const questionTypeOptions = [
    { value: "report_issue", label: texts?.cs_topic_issue },
    { value: "suggest_feature", label: texts?.cs_topic_feature },
  ]

  useEffect(() => {
    const token = PubSub.subscribe("open:customer-support", () => {
      setIsOpen(true)
    })
    return () => PubSub.unsubscribe(token)
  }, [])

  useEffect(() => {
    if (isOpen) return
    setQuestionType("")
    setMessage("")
    setEmail("")
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
    setErrors({})
    setSubmitError(null)
    setIsSubmitting(false)
  }, [isOpen])

  const validateForm = () => {
    const errs = {}
    if (!questionType) errs.questionType = texts?.cs_err_select_topic
    if (!message) errs.message = texts?.cs_err_message
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      errs.email = texts?.cs_err_email_required
    } else if (!emailValidator.validate(trimmedEmail)) {
      errs.email = texts?.cs_err_email_invalid
    }
    if (file && file.size > 20 * 1024 * 1024) {
      errs.file = texts?.cs_err_file_size
    }
    return errs
  }

  const handleSubmit = async () => {
    setSubmitError(null)
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    setIsSubmitting(true)
    try {
      const tagMap = {
        report_issue: "bug",
        suggest_feature: "feature",
      }
      const summaryMap = {
        report_issue: texts?.cs_summary_issue,
        suggest_feature: texts?.cs_summary_feature,
      }
      const summary = t(texts?.cs_summary_template, {
        summary: summaryMap[questionType] || questionType,
        email,
      })
      const response = await SubmitSupportTicket({
        email,
        summary,
        description: message,
        tags: [tagMap[questionType] || questionType],
        files: file ? [file] : [],
      })
      if (response.error) {
        setSubmitError(t(texts?.cs_server_error, { error: response.error }))
        setIsSubmitting(false)
        return
      }
      if (response.result) {
        showNotification({
          message: texts?.cs_thank_you,
          className: "bg-primary text-white",
        })
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ""
        setIsOpen(false)
      }
    } catch {
      setSubmitError(texts?.cs_submit_error)
    }
    setIsSubmitting(false)
  }

  if (!texts) return null

  return (
    <Modal
      variant="snap_medium"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      className="md:w-[560px]"
      zIndex={500}
      title={texts?.cs_title}
      closeOnEscape={true}
      closeOnClickOutside={true}
    >
      {/* Header */}
      <div className="border-b border-bordered py-2 mb-6">
        <Typography 
          variant="txt_dim"
          className="block text-center md:text-left"
        >
          {texts?.cs_subtitle}
        </Typography>
      </div>

      {/* Body */}
      <div className="space-y-4">
        {/* Question type */}
        <div>
          <Typography variant="label_accent">
            {texts?.cs_topic_label}
          </Typography>

          <DropdownSelect
            variant="snap_compact"
            options={questionTypeOptions}
            value={questionTypeOptions.filter((o) => o.value === questionType)}
            onChange={(selection) => setQuestionType(selection[0]?.value || "")}
            placeholder={texts?.cs_select_option}
            showCheckbox={false}
            showSelectAll={false}
            enableResetButton={false}
            styleOverrides={{
              button: `inline-flex h-[40px] justify-between w-full rounded-full px-4 py-2 bg-neutral text-sm font-medium text-text focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-0 ${
                errors.questionType ? "border-error" : "border-bordered focus:border-primary"
              }`,
            }}
          />
          {errors.questionType && (
            <Typography variant="txt1" className="text-error px-1 mt-1">
              {errors.questionType}
            </Typography>
          )}
        </div>

        {/* Email */}
        <div>
          <Typography variant="label_accent">
            {texts?.cs_email_label}
          </Typography>
          <Input
            variant="snap_compact"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={texts?.cs_email_placeholder}
            className={errors.email ? "border-error" : ""}
          />
          {errors.email && (
            <Typography variant="txt1" className="text-error px-1 mt-1">
              {errors.email}
            </Typography>
          )}
        </div>

        {/* Message */}
        <div>
          <Typography variant="label_accent">
            {texts?.cs_message_label}
          </Typography>
          <Input
            multiline
            variant="snap_compact"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={texts?.cs_message_placeholder}
            className="w-full"
            styleOverrides={{
              textarea: "h-28 bg-neutral placeholder-text_secondary/80 resize-none p-4 focus:outline-none focus:ring-0 scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-transparent",
              input_wrapper: `border bg-neutral rounded-2xl focus-within:border-primary transition-colors overflow-hidden ${
                errors.message ? "border-error" : "border-neutral"
              }`,
            }}
          />
          {errors.message && (
            <Typography variant="txt1" className="text-error px-1 mt-1">
              {errors.message}
            </Typography>
          )}
        </div>

        {/* File attachment */}
        <div>
          <Typography variant="label_accent">
            {texts?.cs_attach_label}

            <span className="text-text_secondary font-normal ml-1">
              {texts?.cs_optional}
            </span>
          </Typography>

          <Input
            variant="snap_compact"
            type="file"
            ref={fileInputRef}
            onClick={(e) => { e.target.value = "" }}
            onChange={(e) => {
              const f = e.target.files[0]
              if (f) setFile(f)
            }}
            className="w-full"
          />

          {file && (
            <Typography variant="txt1_dim" className="block mt-1 px-1">
              {t(texts?.cs_attached, { filename: file.name })}
            </Typography>
          )}
          {errors.file && (
            <Typography variant="txt1" className="text-error px-1 mt-1">
              {errors.file}
            </Typography>
          )}
        </div>

        {submitError && (
          <Typography variant="txt1" className="text-error px-1">
            {submitError}
          </Typography>
        )}

        <Button
          onClick={handleSubmit}
          variant="primary_m"
          disabled={isSubmitting}
          className="min-w-[200px] mx-auto disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {texts?.cs_sending}
            </>
          ) : texts?.cs_send}
        </Button>
      </div>
    </Modal>
  )
}

export default CustomerSupportPanel
